import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

export const BookingModal = ({ isOpen, onClose, selectedService, bookingType }) => {
  const [patientName, setPatientName] = useState('');
  const [selectedOption, setSelectedOption] = useState('');

  const departments = [
    'Diabetologist',
    'Ophthalmologist',
    'Cardiologist',
    'Neurologist',
    'Dermatologist',
    'Gynecologist',
    'Psychologist',
    'Orthopedics',
    'Pediatrics (Child Specialist)',
    'Geriatrics',
    'Urologist',
  ];

  const subServices = [
    'ECG (Center: ₹350 | Home: ₹700)',
    'BP Check',
    'Injection',
    'Child Vaccine',
    'Adult Vaccine'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!patientName.trim()) {
      alert('Please enter your name');
      return;
    }

    let message = '';

    if (bookingType === 'department') {
      // Condition A: Specific department (1-11)
      message = `Hi, my name is ${patientName}. I want to book an appointment for the ${selectedService} department.`;
    } else if (bookingType === 'others') {
      // Condition B: Others with sub-services
      if (!selectedOption) {
        alert('Please select a service');
        return;
      }
      message = `Hi, my name is ${patientName}. I need the ${selectedOption} service.`;
    } else if (bookingType === 'global') {
      // Condition C: Global booking
      if (!selectedOption) {
        alert('Please select a service');
        return;
      }
      message = `Hi, my name is ${patientName}. I want to book an appointment for ${selectedOption}.`;
    }

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919007667528?text=${encodedMessage}`, '_blank');
    
    // Reset and close
    setPatientName('');
    setSelectedOption('');
    onClose();
  };

  const handleClose = () => {
    setPatientName('');
    setSelectedOption('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Book Appointment</h2>
              <p className="text-sm text-rose-50 mt-1">Fill in your details below</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Patient Name Input */}
          <div>
            <Label htmlFor="patientName" className="text-slate-700 font-medium mb-2 block">
              Patient Name *
            </Label>
            <Input
              id="patientName"
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Enter your full name"
              className="w-full"
              required
            />
          </div>

          {/* Condition A: Department (Read-only) */}
          {bookingType === 'department' && (
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Department</Label>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                <p className="text-rose-900 font-semibold">{selectedService}</p>
              </div>
            </div>
          )}

          {/* Condition B: Others - Sub-services Selection */}
          {bookingType === 'others' && (
            <div>
              <Label className="text-slate-700 font-medium mb-3 block">Select Service *</Label>
              <div className="grid grid-cols-2 gap-3">
                {subServices.map((service) => (
                  <button
                    key={service}
                    type="button"
                    onClick={() => setSelectedOption(service)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      selectedOption === service
                        ? 'border-rose-600 bg-rose-50 text-rose-900'
                        : 'border-slate-200 hover:border-rose-300 text-slate-700'
                    }`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Condition C: Global - All Services Dropdown */}
          {bookingType === 'global' && (
            <div>
              <Label htmlFor="serviceSelect" className="text-slate-700 font-medium mb-2 block">
                Select Service/Department *
              </Label>
              <select
                id="serviceSelect"
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-500 focus:outline-none"
                required
              >
                <option value="">-- Choose a service --</option>
                <optgroup label="Departments">
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Other Services">
                  {subServices.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Send className="w-5 h-5 mr-2" />
            Send to WhatsApp
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};